import { API_BASE } from '../config';
import { ENTITY_TYPE_LABEL_KEYS, type EntityType } from './entity-utils';

export type MentionRenameImpactSource = {
  sourceType: string;
  sourceId: string;
  sourceDocumentId: string | null;
  label: string;
};

export type MentionRenameImpact = {
  sources: MentionRenameImpactSource[];
};

export type MentionRenameImpactGroup = {
  sourceType: string;
  sources: MentionRenameImpactSource[];
};

export async function fetchMentionRenameImpact(params: {
  documentId: string;
  entityKind: 'characters' | 'locations';
  entityId: string;
  token: string | null;
  masterKey?: string | null;
}): Promise<MentionRenameImpact> {
  const { documentId, entityKind, entityId, token, masterKey } = params;
  const url = masterKey
    ? `${API_BASE}/api/documents/${documentId}/${entityKind}/${entityId}/mention-rename-impact?masterKey=${encodeURIComponent(masterKey)}`
    : `${API_BASE}/api/documents/${documentId}/${entityKind}/${entityId}/mention-rename-impact`;
  const response = await fetch(url, {
    headers: {
      ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!response.ok) {
    throw new Error('Failed to load mention rename impact');
  }
  return response.json() as Promise<MentionRenameImpact>;
}

export function mentionRenameSourceTypeLabelKey(sourceType: string): string {
  return ENTITY_TYPE_LABEL_KEYS[sourceType as EntityType] ?? 'Documents';
}

export function groupMentionRenameSourcesByType(
  sources: MentionRenameImpactSource[],
): MentionRenameImpactGroup[] {
  const byType = new Map<string, MentionRenameImpactSource[]>();
  for (const source of sources) {
    const existing = byType.get(source.sourceType);
    if (existing) {
      existing.push(source);
    } else {
      byType.set(source.sourceType, [source]);
    }
  }

  return [...byType.entries()]
    .sort(([leftType], [rightType]) => leftType.localeCompare(rightType))
    .map(([sourceType, groupSources]) => ({
      sourceType,
      sources: [...groupSources].sort((left, right) => left.label.localeCompare(right.label)),
    }));
}
